import get from 'lodash/get';
import {useEffect,useState} from 'react';
import {useNavigate,useSearchParams} from 'react-router-dom';

import {useGetCurrentProviderQuery} from '../../redux/services/providerApi';
import {useGetSessionsQuery} from '../../redux/services/sessionApi';
import useUser from '../../hooks/useUser';

import AppDashboard from '../AppDashboard';
import ListSessionsScreen from "../../components/sessions/ListSessionsScreen";

const parseSearchParams = searchParams => {
  const allowedFields = [
    'start_time',
    'end_time',
    'patient_id',
    'text',
    'location',
    'include_nonowned_sessions',
    'sort',
    'bookmarked',
    'case_id',
  ];
  const filters = allowedFields.reduce((acc, k) => {
    const val = searchParams.get(k);
    // include_nonowned_sessions should only be set if patient_id is also set
    if (k === 'include_nonowned_sessions' && !searchParams.patient_id) return acc;
    if (val) {
      if (val === 'true') return {...acc, [k]: true};
      if (val === 'false') return {...acc, [k]: false};
      return {...acc, [k]: val};
    }
    return acc;
  }, {sort: '-date'});
  return filters;
};

const tabs = [
  'all',
  'incomplete',
  'upcoming',
  'completed',
  'canceled',
];

const handleFilter = ({setFilters, setSearchParams}) => filter => {
  setSearchParams(filter || {});
  setFilters(filter || {});
};

const handleTabChange = ({setTab, setPageIndex}) => tab => {
  setTab(tab);
  setPageIndex(0);
};

const ListSessionsPage = props => {
  // https://stackoverflow.com/questions/75522048/react-how-to-access-urls-parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(parseSearchParams(searchParams));
  const [pageIndex, setPageIndex] = useState(
    tabs.reduce((acc, next) => ({...acc, [next]: 0}), {}) // set pageIndex to 0
  );
  const [pageSize, setPageSize] = useState(25);
  const [tab, setTab] = useState(tabs[0]);
  const navigate = useNavigate();
  const user = useUser();
  const isPatient = get(user, 'is_patient') || false;
  const providerQuery = useGetCurrentProviderQuery();
  useEffect(() => {
    if (isPatient) setTab(tabs[0]);
    return;
  }, [isPatient])
  const incompleteSessionsQuery = useGetSessionsQuery(
    {skip: pageIndex.incomplete * pageSize || 0, limit: pageSize, ...filters, action_is_needed: true},
    {skip: false}
  );
  const upcomingSessionsQuery = useGetSessionsQuery(
    {skip: pageIndex.upcoming * pageSize || 0, limit: pageSize, ...filters, is_upcoming: true},
    {skip: false}
  );
  const completedSessionsQuery = useGetSessionsQuery(
    {skip: pageIndex.completed * pageSize || 0, limit: pageSize, ...filters, is_completed: true},
    {skip: false}
  );
  const canceledSessionsQuery = useGetSessionsQuery(
    {skip: pageIndex.canceled * pageSize || 0, limit: pageSize, ...filters, is_canceled: true},
    {skip: false}
  );
  const allSessionsQuery = useGetSessionsQuery(
    {skip: pageIndex.all * pageSize || 0, limit: pageSize, ...filters},
    {skip: false}
  );
  const isLoading = (
    providerQuery.isLoading
    || incompleteSessionsQuery.isLoading
    || upcomingSessionsQuery.isLoading
    || completedSessionsQuery.isLoading
  );
  const onTabChange = handleTabChange({setTab, setPageIndex});
  const queries = {
    incomplete: incompleteSessionsQuery,
    upcoming: upcomingSessionsQuery,
    completed: completedSessionsQuery,
    canceled: canceledSessionsQuery,
    all: allSessionsQuery,
  };
  const childProps = {
    ...props,
    // data
    sessions: tabs.reduce((acc, t) => ({
      ...acc,
      [t]: get(queries, `${t}.data.data`) || [],
    }), {}),
    counts: tabs.reduce((acc, t) => ({
      ...acc,
      [t]: get(queries, `${t}.data.count`) || 0,
    }), {}),
    queries,
    isLoading: queries?.[tab]?.isLoading,
    isUpdating: queries?.[tab]?.isFetching || queries?.[tab]?.isLoading,
    isPatient,
    // tabs
    tab,
    tabs,
    onTabChange,
    // pagination
    pageIndex,
    pageSize,
    filters,
    showAllProviders: !isPatient && (filters?.include_nonowned_sessions || false),
    // handlers
    onFilterChange: handleFilter({setFilters, setSearchParams}),
    onPageChange: category => (e,num) => setPageIndex({...pageIndex, [category]: num}),
    onRowsPerPageChange: e => setPageSize(parseInt(e.target.value, 10)),
    onEditSession: () => console.log('implement onMessagePatient!'),
    onMessagePatient: id => navigate(`/providers/patients/messages/${id}`),
  };
  return (
    <AppDashboard title='Sessions' loading={isLoading} showTitle showHomeBtn>
      <ListSessionsScreen {...childProps} />
    </AppDashboard>
  );
};

export default ListSessionsPage;
